import { CircularProgress, Stack } from '@mui/material';

import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { ActionCard } from '@/components/layout/ActionCard/ActionCard';
import { AnnotatedPDFViewerProvider } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.provider';
import { COLORS } from '@/styles/tokens/colors';

import { DocumentSummarySideContent } from '../chatSideContent.types';
import { ChatDocumentViewer } from '../documentViewer/ChatDocumentViewer';
import { useChatDocumentViewerSummaryQuery } from '../documentViewer/graphql/ChatDocumentViewer.generated';

function useDocumentSummaryNode(documentSummaryId: string) {
  const { data, loading, error } = useChatDocumentViewerSummaryQuery({
    variables: { documentSummaryId },
  });
  const summaryNode =
    data?.node?.__typename === 'DocumentSummary' ? data.node : null;
  return { summaryNode, loading, error: Boolean(error) };
}

/**
 * Pane title for a referenced document summary: the parent document's name.
 * Runs the same query as the body (Apollo dedupes), falling back to a generic
 * title while it loads or if the summary can't be resolved.
 */
export function DocumentSummarySideContentTitle({
  content,
}: {
  content: DocumentSummarySideContent;
}) {
  const { summaryNode } = useDocumentSummaryNode(content.documentSummaryId);
  return <>{summaryNode?.documentv2.name ?? 'Document summary'}</>;
}

/**
 * Shows a document summary referenced by a chat message, opened on the
 * viewer's Summary tab. The Document tab shows the summary's parent document,
 * so the reader can jump from the summary to the underlying source.
 */
export function DocumentSummarySideContentBody({
  content,
}: {
  content: DocumentSummarySideContent;
}) {
  const { summaryNode, loading, error } = useDocumentSummaryNode(
    content.documentSummaryId
  );

  if (loading) {
    return (
      <Stack
        sx={{ alignItems: 'center', height: '100%', justifyContent: 'center' }}
      >
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (error || !summaryNode) {
    return (
      <Stack justifyContent="center" height="100%" width="100%" p={6}>
        <ActionCard
          variant="noCard"
          heading="Unable to load summary"
          description="The referenced document summary could not be loaded"
          sx={{ backgroundColor: 'transparent !important' }}
          icon={<FileSearch02Icon size={40} sx={{ color: COLORS.GRAY[300] }} />}
        />
      </Stack>
    );
  }

  return (
    <AnnotatedPDFViewerProvider
      showCitations
      // The coachmark's "prev/next below the document" instructions describe
      // the document-details layout, not this pane
      showCitationCoachmark={false}
      documentId={summaryNode.documentv2.id}
    >
      <ChatDocumentViewer
        initialTab="summary"
        summaryOverride={summaryNode.summary}
      />
    </AnnotatedPDFViewerProvider>
  );
}
